import {Component, forwardRef, Input, OnDestroy, OnInit, ViewEncapsulation} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  FormControl,
  FormGroup,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import {Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, takeUntil} from 'rxjs/operators';
import {IconType, IoniconsOutline, PasswordInputState, WidgetValidator} from '@pt/pt-models';
import {PasswordUtils} from '../../../utils';
import {PasswordParamsResponse} from '@primatoo-ms-organization';
import {AsyncValidator, ValidatorGenerator} from '../../utils';
import {CheckPasswordService} from "@pt/api-client";

@Component({
  selector: 'pt-input-password',
  templateUrl: './input-password.component.html',
  styleUrls: ['./input-password.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      multi: true,
      useExisting: forwardRef(() => InputPasswordComponent),
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => InputPasswordComponent),
      multi: true,
    },
  ],
  encapsulation: ViewEncapsulation.None,
})
export class InputPasswordComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  @Input() controlName: string;
  @Input() validators: WidgetValidator[];
  @Input() label: string;
  @Input() placeholder: string;
  @Input() help: boolean;
  @Input() generateEnabled = false;
  @Input() useComponentStyle = true;
  @Input() passwordParamsResponse: PasswordParamsResponse;
  @Input() showRequiredSign = true;
  @Input() checkPasswordService: CheckPasswordService;

  public iconKey = IoniconsOutline.infoCircle;
  public iconType = IconType.IONIC_ICON;

  public passwordInputState = new PasswordInputState();
  public isChanged = false;
  public isRequired: boolean;
  public formGroup: FormGroup;
  public isDisabled = false;

  public inputId: string;
  private destroy$: Subject<boolean> = new Subject<boolean>();
  private onChanged: (val) => void;
  private onTouched: () => void;

  get password() {
    return this.formGroup?.controls['password'];
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }

  ngOnInit(): void {
    this.inputId = 'ptWInputPassword_' + this.controlName;

    this.isRequired = ValidatorGenerator.validatorsContainsRequired(
      this.validators
    );

    this.setFormGroup();

    this.formGroup.statusChanges
      .pipe(debounceTime(50), distinctUntilChanged())
      .subscribe(() => {
        this.formGroup.updateValueAndValidity();
      });

    this.formGroup.valueChanges
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe((t) => {
        this.isChanged = t.password !== null;
        if (this.onChanged) {
          this.onChanged(t.password);
        }
        if (this.onTouched) {
          this.onTouched();
        }
      });
  }

  writeValue(obj: any): void {
    this.formGroup.controls.password.setValue(obj);
  }

  registerOnChange(fn: any): void {
    this.onChanged = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    if (this.formGroup?.controls) {
      if (isDisabled) {
        if (this.formGroup.controls.password.enabled) {
          this.formGroup.controls.password.disable();
        }
      } else {
        if (this.formGroup.controls.password.disabled) {
          this.formGroup.controls.password.enable();
        }
      }
    }
  }

  validate(c: AbstractControl) {
    return !this.formGroup.controls.password.errors ? null : { invalid: true };
  }

  generatePassword() {
    this.formGroup.controls.password.setValue(
      PasswordUtils.generatePassword(this.passwordParamsResponse)
    );
  }

  private setFormGroup() {
    const validator = ValidatorGenerator.generateValidator(this.validators);
    const asyncValidator = this.checkPasswordService
      ? AsyncValidator.checkPassword(this.checkPasswordService)
      : null;
    this.formGroup = new FormGroup({
      password: new FormControl(null, validator, asyncValidator),
    });
  }
}

